import React, { useRef, useEffect, useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faDownload,
	faCopy,
	faTrash,
	faEye,
} from '@fortawesome/free-solid-svg-icons';
import styles from './ImageModal.module.css';
import UserData from '../../../services/UserData';
import Permissions from '../../../services/Permissions';
import AIImageService from '../../../services/AIImageService';
import Spinner from '../../../shared_components/Spinner/Spinner';

const ImageModal = ({
	isOpen,
	onClose,
	prompt,
	onDownload,
	onCopy,
	onEdit,
	onDelete,
	children,
	imageId,
	assetName,
}) => {
	const downloadRef = useRef(null);
	const userData = useContext(UserData);
	const [isImageLoading, setIsImageLoading] = useState(true);
	const [shouldRenderImage, setShouldRenderImage] = useState(false);
	const [isImageError, setIsImageError] = useState(false);

	useEffect(() => {
		let timeoutId;
		if (isOpen) {
			setIsImageLoading(true);
			setShouldRenderImage(false);
			setIsImageError(false);

			timeoutId = setTimeout(() => {
				setShouldRenderImage(true);
			}, 100);
		} else {
			setShouldRenderImage(false);
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [isOpen]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (downloadRef.current && !downloadRef.current.contains(event.target)) {
				// No need to set showDownloadOptions to false as it's removed
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleImageLoad = () => {
		setIsImageLoading(false);
		setIsImageError(false);
	};

	const handleImageError = () => {
		if (!isImageLoading) {
			setIsImageError(true);
		}
	};

	const handleDelete = () => {
		if (window.confirm('Are you sure you want to delete this image?')) {
			if (typeof onDelete !== 'function') {
				alert('Error: onDelete is not a function');
				return;
			}
			onDelete(imageId)
				.then(() => {
					onClose();
				})
				.catch((error) => {
					alert('Failed to delete the image. Please try again.');
				});
		}
	};

	const deleteButtonElement = Permissions.displayIfAnyOfTheRolesAreAllowed(
		userData,
		AIImageService.deleteRoles,
		<button className={styles.deleteButton} onClick={handleDelete}>
			<FontAwesomeIcon icon={faTrash} /> Delete
		</button>
	);

	if (!isOpen) return null;

	return (
		<div className={styles.modalOverlay} onClick={onClose}>
			<div className={styles.modal}>
				<div
					className={styles.modalContent}
					onClick={(e) => e.stopPropagation()}>
					<span className={styles.closeButton} onClick={onClose}>
						&times;
					</span>
					<div className={styles.mainContent}>
						<div className={styles.imageContainer}>
							{(isImageLoading || !shouldRenderImage) && !isImageError && (
								<div className={styles.spinnerOverlay}>
									<Spinner />
								</div>
							)}
							{isImageError && (
								<div className={styles.errorOverlay}>
									<p>Failed to load image. Please try again later.</p>
								</div>
							)}
							{shouldRenderImage &&
								React.Children.map(children, (child) =>
									React.cloneElement(child, {
										onLoad: handleImageLoad,
										onError: handleImageError,
										style: {
											visibility: isImageLoading ? 'hidden' : 'visible',
										},
									})
								)}
						</div>
						<div className={styles.content}>
							<h3>PROMPT</h3>
							<p>{prompt}</p>
							<div className={styles.buttonGroup}>
								<button onClick={onDownload}>
									<FontAwesomeIcon icon={faDownload} /> Download
								</button>
								<button onClick={onCopy}>
									<FontAwesomeIcon icon={faCopy} /> Copy Prompt
								</button>
								<button onClick={onEdit}>
									<FontAwesomeIcon icon={faEye} /> View
								</button>
								{deleteButtonElement}
							</div>
							{assetName && (
								<div className={styles.assetName}>
									<p>{assetName}</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ImageModal;
